import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { login } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND } from "../lib/constants";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || submitting) return;
    setSubmitting(true);
    try {
      await login(password);
      toast.dismiss();
      await refresh();
      const next = params.get("next") || "/dashboard";
      navigate(next, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error("Too many attempts. Try again in 15 minutes.");
      else toast.error("Incorrect password");
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[var(--tir-navy)] text-white px-12 py-14 w-1/2 relative overflow-hidden">
        <div className="flex items-center gap-4">
          <img
            src={BRAND.logoUrl}
            alt={`${BRAND.name} logo`}
            className="h-12 w-auto object-contain"
            data-testid="login-logo"
          />
          <div className="leading-tight">
            <div className="font-eyebrow text-[10px] tracking-[0.35em] uppercase text-white/70">
              {BRAND.name}
            </div>
            <div className="font-serif-display text-xl text-white">{BRAND.tagline}</div>
          </div>
        </div>
        <div>
          <div className="font-eyebrow text-[11px] tracking-[0.32em] uppercase text-white/60 mb-4">
            Coach Studio
          </div>
          <h1 className="font-serif-display text-5xl xl:text-6xl leading-[1.05]">
            Your private lactate testing studio.
          </h1>
          <p className="text-base text-white/70 mt-5 max-w-md leading-relaxed">
            Plan tests, build athlete reports, and track threshold progress in one place. This
            studio is for {BRAND.name} only.
          </p>
        </div>
        <div className="font-eyebrow text-[10px] tracking-[0.3em] uppercase text-white/50">
          {BRAND.website}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-16 bg-[var(--tir-ivory)]">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md"
          data-testid="login-form"
          autoComplete="off"
        >
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img
              src={BRAND.logoUrl}
              alt=""
              className="h-10 w-auto object-contain"
              style={{ filter: "invert(1)" }}
            />
            <div className="font-serif-display text-lg">{BRAND.name}</div>
          </div>
          <div className="font-eyebrow text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
            Coach Sign-in
          </div>
          <h2 className="font-serif-display text-4xl text-[var(--tir-ink)] leading-tight mb-2">
            Welcome back, Coach.
          </h2>
          <p className="text-sm text-[var(--tir-muted)] mb-10 leading-relaxed">
            Enter your password to access the studio.
          </p>

          <label className="font-eyebrow block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
            Password
          </label>
          <div className="relative">
            <Lock
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tir-muted)]"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              autoComplete="off"
              data-testid="login-password-input"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] pl-10 pr-3 py-3 text-base focus:outline-none font-mono-tir"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !password.trim()}
            data-testid="login-submit-btn"
            className="mt-7 w-full bg-[var(--tir-burgundy)] text-white py-3 font-eyebrow text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-xs text-[var(--tir-muted)] mt-6 leading-relaxed">
            Sessions stay signed in on this device for 30 days. After 5 wrong attempts, sign-in is
            paused for 15 minutes.
          </p>
        </form>
      </div>
    </div>
  );
}
